import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { SubcontractorUser } from '../../planning/types';
import type { SettingsSubcontractorRow } from '../types';

const userName = (u: SubcontractorUser) => `${u.first_name} ${u.last_name}`;

interface UseManageSubcontractorsReturn {
  subcontractors: SettingsSubcontractorRow[];
  archivedSubcontractors: SettingsSubcontractorRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  addSubcontractor: (name: string) => Promise<boolean>;
  updateSubcontractor: (id: string, name: string) => Promise<boolean>;
  archiveSubcontractor: (id: string) => Promise<boolean>;
  unarchiveSubcontractor: (id: string) => Promise<boolean>;
  deleteSubcontractor: (id: string) => Promise<'ok' | 'has_bookings' | 'error'>;
  addUser: (subcontractorId: string, firstName: string, lastName: string) => Promise<boolean>;
  updateUser: (id: string, firstName: string, lastName: string) => Promise<boolean>;
  deleteUser: (id: string, subcontractorId: string) => Promise<boolean>;
  setDefaultUser: (userId: string, subcontractorId: string) => Promise<boolean>;
}

async function buildRows(data: SettingsSubcontractorRow[]): Promise<SettingsSubcontractorRow[]> {
  const ids = data.map((s) => s.id);
  const { data: bookingRows } = ids.length
    ? await supabase.from('bookings').select('subcontractor_id').in('subcontractor_id', ids)
    : { data: [] };
  const withBookings = new Set((bookingRows ?? []).map((b) => b.subcontractor_id));
  return data.map((s) => ({
    ...s,
    subcontractor_users: sortByName(s.subcontractor_users ?? [], userName),
    has_bookings: withBookings.has(s.id),
  }));
}

export function useManageSubcontractors(orgId: string | null): UseManageSubcontractorsReturn {
  const [subcontractors, setSubcontractors] = useState<SettingsSubcontractorRow[]>([]);
  const [archivedSubcontractors, setArchivedSubcontractors] = useState<SettingsSubcontractorRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const loadActive = async () => {
    if (!orgId) { setSubcontractors([]); return; }
    try {
      const { data } = await supabase
        .from('subcontractors')
        .select('*, subcontractor_users(*)')
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('name');
      setSubcontractors(await buildRows(data ?? []));
    } catch {
      setSubcontractors([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data } = await supabase
        .from('subcontractors')
        .select('*, subcontractor_users(*)')
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('name');
      setArchivedSubcontractors(await buildRows(data ?? []));
      return true;
    } catch {
      setArchivedSubcontractors([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedSubcontractors([]);
    loadActive();
  }, [orgId]);

  const addSubcontractor = async (name: string): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { error } = await supabase
        .from('subcontractors')
        .insert({ name, organization_id: orgId, archived: false });
      if (error) throw error;
      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateSubcontractor = async (id: string, name: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('subcontractors').update({ name }).eq('id', id);
      if (error) throw error;
      const update = (list: SettingsSubcontractorRow[]) =>
        sortByName(list.map((s) => (s.id === id ? { ...s, name } : s)));
      setSubcontractors(update);
      setArchivedSubcontractors(update);
      return true;
    } catch {
      return false;
    }
  };

  const archiveSubcontractor = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('subcontractors').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setSubcontractors((prev) => prev.filter((s) => s.id !== id));
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch { return false; }
  };

  const unarchiveSubcontractor = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('subcontractors').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedSubcontractors((prev) => prev.filter((s) => s.id !== id));
      await loadActive();
      return true;
    } catch { return false; }
  };

  const deleteSubcontractor = async (id: string): Promise<'ok' | 'has_bookings' | 'error'> => {
    try {
      const { count } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('subcontractor_id', id);
      if ((count ?? 0) > 0) return 'has_bookings';
      const { error } = await supabase.from('subcontractors').delete().eq('id', id);
      if (error) throw error;
      setSubcontractors((prev) => prev.filter((s) => s.id !== id));
      setArchivedSubcontractors((prev) => prev.filter((s) => s.id !== id));
      return 'ok';
    } catch {
      return 'error';
    }
  };

  const addUser = async (
    subcontractorId: string,
    firstName: string,
    lastName: string,
  ): Promise<boolean> => {
    try {
      const allSubs = [...subcontractors, ...archivedSubcontractors];
      const existing = allSubs.find((s) => s.id === subcontractorId)?.subcontractor_users ?? [];
      const isDefault = existing.length === 0;
      const { error } = await supabase.from('subcontractor_users').insert({
        subcontractor_id: subcontractorId,
        first_name: firstName,
        last_name: lastName,
        is_default: isDefault,
      });
      if (error) throw error;
      const isArchived = archivedSubcontractors.some((s) => s.id === subcontractorId);
      if (isArchived) await refreshArchived(); else await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateUser = async (id: string, firstName: string, lastName: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('subcontractor_users')
        .update({ first_name: firstName, last_name: lastName })
        .eq('id', id);
      if (error) throw error;
      const update = (list: SettingsSubcontractorRow[]) =>
        list.map((s) => ({
          ...s,
          subcontractor_users: sortByName(
            s.subcontractor_users.map((u) =>
              u.id === id ? { ...u, first_name: firstName, last_name: lastName } : u,
            ),
            userName,
          ),
        }));
      setSubcontractors(update);
      setArchivedSubcontractors(update);
      return true;
    } catch {
      return false;
    }
  };

  const deleteUser = async (id: string, subcontractorId: string): Promise<boolean> => {
    try {
      const allSubs = [...subcontractors, ...archivedSubcontractors];
      const sub = allSubs.find((s) => s.id === subcontractorId);
      const user = sub?.subcontractor_users.find((u) => u.id === id);
      const { error } = await supabase.from('subcontractor_users').delete().eq('id', id);
      if (error) throw error;
      if (user?.is_default) {
        const remaining = (sub?.subcontractor_users ?? []).filter((u) => u.id !== id);
        if (remaining.length > 0) {
          await supabase
            .from('subcontractor_users')
            .update({ is_default: true })
            .eq('id', remaining[0].id);
        }
      }
      const isArchived = archivedSubcontractors.some((s) => s.id === subcontractorId);
      if (isArchived) await refreshArchived(); else await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const setDefaultUser = async (userId: string, subcontractorId: string): Promise<boolean> => {
    try {
      const { error: clearError } = await supabase
        .from('subcontractor_users')
        .update({ is_default: false })
        .eq('subcontractor_id', subcontractorId);
      if (clearError) throw clearError;
      const { error: setError } = await supabase
        .from('subcontractor_users')
        .update({ is_default: true })
        .eq('id', userId);
      if (setError) throw setError;
      const update = (list: SettingsSubcontractorRow[]) =>
        list.map((s) =>
          s.id !== subcontractorId
            ? s
            : {
                ...s,
                subcontractor_users: s.subcontractor_users.map((u) => ({
                  ...u,
                  is_default: u.id === userId,
                })),
              },
        );
      setSubcontractors(update);
      setArchivedSubcontractors(update);
      return true;
    } catch { return false; }
  };

  return {
    subcontractors,
    archivedSubcontractors,
    archivedLoaded,
    loadArchived,
    addSubcontractor,
    updateSubcontractor,
    archiveSubcontractor,
    unarchiveSubcontractor,
    deleteSubcontractor,
    addUser,
    updateUser,
    deleteUser,
    setDefaultUser,
  };
}
